import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    Input,
    message,
    Row,
    Col,
    Divider,
    Space,
} from "antd";
import dayjs from "dayjs";
import api from "../../api";
import { LeftOutlined } from "@ant-design/icons";
import { boldLabelStyle, maxW700, NoWrap } from "../../styles/styles";

const { Title } = Typography;
const { Option } = Select;

const FlightDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [flight, setFlight] = useState<any>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();

    const inDepartureDate = Form.useWatch("in_departure_date", form);
    const inArrivalDate = Form.useWatch("in_arrival_date", form);
    const outDepartureDate = Form.useWatch("out_departure_date", form);
    const outArrivalDate = Form.useWatch("out_arrival_date", form);
    const selectedCurrency = Form.useWatch("flight_cost_currency", form);

    const [manuallyEditedInArrival, setManuallyEditedInArrival] = useState(false);
    const [manuallyEditedOutArrival, setManuallyEditedOutArrival] = useState(false);

    useEffect(() => {
        fetchFlight();
    }, [id]);

    useEffect(() => {
        if (inDepartureDate && !manuallyEditedInArrival) {
            form.setFieldsValue({ in_arrival_date: inDepartureDate });
        }
    }, [inDepartureDate, manuallyEditedInArrival, form]);

    useEffect(() => {
        if (
            inArrivalDate &&
            inDepartureDate &&
            !inArrivalDate.isSame(inDepartureDate, "day")
        ) {
            setManuallyEditedInArrival(true);
        }
    }, [inArrivalDate, inDepartureDate]);

    useEffect(() => {
        if (outDepartureDate && !manuallyEditedOutArrival) {
            form.setFieldsValue({ out_arrival_date: outDepartureDate });
        }
    }, [outDepartureDate, manuallyEditedOutArrival, form]);

    useEffect(() => {
        if (
            outArrivalDate &&
            outDepartureDate &&
            !outArrivalDate.isSame(outDepartureDate, "day")
        ) {
            setManuallyEditedOutArrival(true);
        }
    }, [outArrivalDate, outDepartureDate]);

    const fetchFlight = async () => {
        try {
            const res = await api.get(`/daia/flights/${id}/`);
            setFlight(res.data);

            form.setFieldsValue({
                ...res.data,
                in_departure_date: res.data.in_departure_date ? dayjs(res.data.in_departure_date) : null,
                in_arrival_date: res.data.in_arrival_date ? dayjs(res.data.in_arrival_date) : null,
                out_departure_date: res.data.out_departure_date ? dayjs(res.data.out_departure_date) : null,
                out_arrival_date: res.data.out_arrival_date ? dayjs(res.data.out_arrival_date) : null,
                in_departure_time: res.data.in_departure_time ? dayjs(res.data.in_departure_time, "HH:mm:ss") : null,
                in_arrival_time: res.data.in_arrival_time ? dayjs(res.data.in_arrival_time, "HH:mm:ss") : null,
                out_departure_time: res.data.out_departure_time ? dayjs(res.data.out_departure_time, "HH:mm:ss") : null,
                out_arrival_time: res.data.out_arrival_time ? dayjs(res.data.out_arrival_time, "HH:mm:ss") : null,
            });
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch DAIA flight.");
        }
    };

    const handleUpdate = async (values: any) => {
        try {
            const {
                in_departure_date,
                in_arrival_date,
                out_departure_date,
                out_arrival_date,
                in_departure_time,
                in_arrival_time,
                out_departure_time,
                out_arrival_time,
            } = values;

            if (
                in_departure_date &&
                in_arrival_date &&
                in_departure_date.isAfter(in_arrival_date)
            ) {
                return message.error("IN Departure date must be before or equal to IN Arrival date");
            }

            if (
                in_arrival_date &&
                out_departure_date &&
                !out_departure_date.isAfter(in_arrival_date)
            ) {
                return message.error("OUT Departure date must be after IN Arrival date");
            }

            if (
                out_departure_date &&
                out_arrival_date &&
                out_departure_date.isAfter(out_arrival_date)
            ) {
                return message.error("OUT Departure date must be before or equal to OUT Arrival date");
            }

            if (
                in_departure_time &&
                in_arrival_time &&
                !in_departure_time.isBefore(in_arrival_time)
            ) {
                return message.error("IN Departure time must be before IN Arrival time");
            }

            if (
                out_departure_time &&
                out_arrival_time &&
                !out_departure_time.isBefore(out_arrival_time)
            ) {
                return message.error("OUT Departure time must be before OUT Arrival time");
            }

            const payload = {
                ...values,
                customer_id: flight.customer.id,
                in_departure_date: in_departure_date?.format("YYYY-MM-DD") || null,
                in_arrival_date: in_arrival_date?.format("YYYY-MM-DD") || null,
                out_departure_date: out_departure_date?.format("YYYY-MM-DD") || null,
                out_arrival_date: out_arrival_date?.format("YYYY-MM-DD") || null,
                in_departure_time: in_departure_time?.format("HH:mm:ss") || null,
                in_arrival_time: in_arrival_time?.format("HH:mm:ss") || null,
                out_departure_time: out_departure_time?.format("HH:mm:ss") || null,
                out_arrival_time: out_arrival_time?.format("HH:mm:ss") || null,
                flight_cost: values.flight_cost
                    ? parseInt(values.flight_cost.toString().replace(/,/g, ""))
                    : null,
            };

            await api.put(`/daia/flights/${flight.id}/`, payload);

            message.success("DAIA flight updated");
            setIsModalVisible(false);
            fetchFlight();
        } catch (err) {
            console.error(err);
            message.error("Failed to update DAIA flight.");
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: "Are you sure you want to delete this flight reservation?",
            content: "This action cannot be undone.",
            okText: "Yes, delete it",
            okType: "danger",
            cancelText: "Cancel",
            centered: true,
            onOk: async () => {
                try {
                    await api.delete(`/daia/flights/${id}/`);
                    message.success("DAIA flight reservation deleted");
                    navigate("/daia/flights");
                } catch (err) {
                    console.error(err);
                    message.error("Failed to delete DAIA flight reservation");
                }
            },
        });
    };

    if (!flight) return null;

    return (
        <div style={{ padding: 24 }}>
            <Button
                icon={<LeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ marginBottom: 24 }}
                className="rounded-md"
            >
                Back
            </Button>

            <Title level={3} style={{ marginBottom: 16 }}>
                DAIA Flight Details
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{ margin: 0 }}>
                                DAIA Flight Overview
                            </Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button danger onClick={handleDelete} className="rounded-md">
                                    Delete
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() => setIsModalVisible(true)}
                                    className="rounded-md"
                                >
                                    Edit
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                }
            >
                <Descriptions bordered column={1} style={maxW700}>
                    <Descriptions.Item label="Customer" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.customer?.first_name} {flight.customer?.last_name}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="MMID" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.customer?.mmid || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Status" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.status || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Reservation Number" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.reservation_number || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Memo" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.memo || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Cabin" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.cabin || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Departure Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_departure_date || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Arrival Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_arrival_date || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Flight Number" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_flight_number || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Departure Airport" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_departure_airport || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Departure Time" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.in_departure_time
                                ? dayjs(flight.in_departure_time, "HH:mm:ss").format("HH:mm")
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Arrival Airport" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_arrival_airport || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Arrival Time" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.in_arrival_time
                                ? dayjs(flight.in_arrival_time, "HH:mm:ss").format("HH:mm")
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Departure Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_departure_date || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Arrival Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_arrival_date || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Flight Number" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_flight_number || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Departure Airport" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_departure_airport || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Departure Time" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.out_departure_time
                                ? dayjs(flight.out_departure_time, "HH:mm:ss").format("HH:mm")
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Arrival Airport" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_arrival_airport || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="OUT Arrival Time" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.out_arrival_time
                                ? dayjs(flight.out_arrival_time, "HH:mm:ss").format("HH:mm")
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Flight Cost Currency" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.flight_cost_currency || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Flight Cost" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.flight_cost != null
                                ? Number(flight.flight_cost).toLocaleString()
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Exchange Rate" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.flight_cost_exchange_rate || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Total Flight Cost (KRW)" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.total_flight_cost_krw != null
                                ? `${Math.round(Number(flight.total_flight_cost_krw)).toLocaleString()} KRW`
                                : "-"}
                        </NoWrap>
                    </Descriptions.Item>
                </Descriptions>

                <Modal
                    title="Edit DAIA Flight"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    centered
                    width={800}
                    okButtonProps={{ className: "rounded-md" }}
                    cancelButtonProps={{ className: "rounded-md" }}
                    bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
                >
                    <Form layout="vertical" form={form} onFinish={handleUpdate}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="status"
                                    label="Status"
                                    rules={[{ required: true, message: "Status is required" }]}
                                >
                                    <Select>
                                        <Option value="Not Started">Not Started</Option>
                                        <Option value="Flight Options Sent">Flight Options Sent</Option>
                                        <Option value="Waiting for Ticketing">Waiting for Ticketing</Option>
                                        <Option value="E-Ticket Sent">E-Ticket Sent</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item name="reservation_number" label="Reservation Number">
                                    <Input placeholder="Enter reservation number" />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item name="cabin" label="Cabin">
                                    <Select allowClear>
                                        <Option value="Economy">Economy</Option>
                                        <Option value="Prestige">Prestige</Option>
                                        <Option value="Business">Business</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">IN (→ Korea)</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="in_departure_date" label="Departure Date">
                                    <DatePicker style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item name="in_arrival_date" label="Arrival Date">
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabledDate={(current) =>
                                            inDepartureDate
                                                ? current && current.isBefore(inDepartureDate, "day")
                                                : false
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="in_flight_number" label="Flight Number">
                            <Input />
                        </Form.Item>

                        <Form.Item name="in_departure_airport" label="Departure Airport">
                            <Input />
                        </Form.Item>

                        <Form.Item name="in_departure_time" label="Departure Time">
                            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                        </Form.Item>

                        <Form.Item name="in_arrival_airport" label="Arrival Airport">
                            <Select allowClear>
                                <Option value="ICN-T1">ICN-T1</Option>
                                <Option value="ICN-T2">ICN-T2</Option>
                                <Option value="GMP">GMP</Option>
                                <Option value="GMP-Private">GMP-Private</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="in_arrival_time" label="Arrival Time">
                            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                        </Form.Item>

                        <Divider orientation="left">OUT (Korea →)</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="out_departure_date" label="Departure Date">
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabledDate={(current) =>
                                            inArrivalDate
                                                ? current && current.isBefore(inArrivalDate, "day")
                                                : false
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item name="out_arrival_date" label="Arrival Date">
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabledDate={(current) =>
                                            outDepartureDate
                                                ? current && current.isBefore(outDepartureDate, "day")
                                                : false
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="out_flight_number" label="Flight Number">
                            <Input />
                        </Form.Item>

                        <Form.Item name="out_departure_airport" label="Departure Airport">
                            <Select allowClear>
                                <Option value="ICN-T1">ICN-T1</Option>
                                <Option value="ICN-T2">ICN-T2</Option>
                                <Option value="GMP">GMP</Option>
                                <Option value="GMP-Private">GMP-Private</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="out_departure_time" label="Departure Time">
                            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                        </Form.Item>

                        <Form.Item name="out_arrival_airport" label="Arrival Airport">
                            <Input />
                        </Form.Item>

                        <Form.Item name="out_arrival_time" label="Arrival Time">
                            <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                        </Form.Item>

                        <Form.Item name="memo" label="Memo">
                            <Input.TextArea placeholder="Enter memo" rows={3} />
                        </Form.Item>

                        <Divider orientation="left">Cost</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="flight_cost_currency" label="Currency">
                                    <Select placeholder="Select currency" allowClear>
                                        <Option value="Yen">Yen</Option>
                                        <Option value="KRW">KRW</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="flight_cost"
                                    label="Flight Cost"
                                    getValueFromEvent={(e) =>
                                        parseInt(e.target.value.replace(/,/g, ""))
                                    }
                                >
                                    <Input
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/,/g, "");
                                            const numeric = rawValue.replace(/\D/g, "");

                                            form.setFieldsValue({
                                                flight_cost: numeric
                                                    ? Number(numeric).toLocaleString()
                                                    : "",
                                            });
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {selectedCurrency === "Yen" && (
                            <Form.Item
                                name="flight_cost_exchange_rate"
                                label="Exchange Rate (Yen ➝ KRW)"
                                rules={[{ required: true, message: "Exchange rate required for Yen" }]}
                            >
                                <Input type="number" step="0.01" />
                            </Form.Item>
                        )}
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default FlightDetailPage;